import { SPEC_TITLE_MAX_LENGTH, sliceCodeUnits } from '@parcel/types';
import type { FeedRow } from '../../lib/feed.js';
import type { Stage } from '../../lib/projects-display.js';
import { stageMeta } from '../../lib/projects-display.js';
import { formatSignalCalendarDate } from '../../lib/signal-dates.js';
import type { ApiSignalType } from '../../lib/signals.js';
import { getSignalMeta } from '../entity-drawer/signal-feed.js';

const MONTH_DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const MONTH_DAY_YEAR = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

/** A project's stage label, or an account's "N active projects". */
export function statusLabel(row: Pick<FeedRow, 'dataset' | 'status'>): string {
  if (!row.status) return '—';
  return row.dataset === 'project' ? stageMeta(row.status as Stage).label : row.status;
}

/** "Oct 2" this year, "Dec 30, 2025" before it, in the member's time zone. */
export function shortDate(iso: string, now = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.getFullYear() === now.getFullYear()
    ? MONTH_DAY.format(date)
    : MONTH_DAY_YEAR.format(date);
}

/** The why-now box: the headline's summary, else what kind of news it is (accounts never carry documents). */
export function whyNowText(row: Pick<FeedRow, 'dataset' | 'headline'>): string {
  const headline = row.headline;
  if (headline?.summary) return headline.summary;
  if (!headline) return 'New documents were found on this project.';
  const label = getSignalMeta(headline.signal_type as ApiSignalType).label;
  return row.dataset === 'account' && headline.project_name
    ? `${label} on ${headline.project_name}.`
    : `${label}.`;
}

/** The name the Feed greets by: the first name, else the first word of the display name. */
export function feedFirstName(
  profile: { firstName?: string | null; displayName?: string | null } | null | undefined,
): string | null {
  const first = profile?.firstName?.trim();
  if (first) return first;
  return profile?.displayName?.trim().split(/\s+/)[0] || null;
}

/** Ask Spec's first message about a row: the question, then one line per signal or document it carries. */
export function askSpecStarter(row: Pick<FeedRow, 'name' | 'items'>): string {
  const lines = row.items.map((item) => {
    const project = item.project_name ?? 'Unnamed project';
    if (item.kind === 'document') {
      return `- ${item.label ?? item.document_type ?? 'Document'} · ${project} · found ${shortDate(item.discovered_at)}`;
    }
    const label = getSignalMeta(item.signal_type as ApiSignalType).label;
    const facts = `- ${label} · ${project} · filed ${formatSignalCalendarDate(item.filed_at)}`;
    return item.summary ? `${facts}: ${item.summary}` : facts;
  });
  return [`What should I know about ${row.name} this week? Here is what changed:`, ...lines].join(
    '\n',
  );
}

/** A chat title the Spec API accepts: the record's name, within its limit, or "New chat" when it has none. */
export function specChatTitle(name: string): string {
  return sliceCodeUnits(name.trim(), SPEC_TITLE_MAX_LENGTH).trim() || 'New chat';
}
